import { useState } from 'react'
import { InlineSpinner } from './components/InlineSpinner'

export type CompanyErpPickerItem = {
  id: string
  label: string
  fileExt?: string
  fileName?: string
}

type Props = {
  items: CompanyErpPickerItem[]
  openingFileId?: string | null
  onOpenFile: (item: CompanyErpPickerItem) => void | Promise<void>
  onPlanSelected?: (item: CompanyErpPickerItem) => void
}

/**
 * Mobile Company Response ERP parity: plan field + VIEW SELECTED ERP button.
 * Multiple plans use a select; a single plan shows the title in a read-only field.
 */
export function CompanyErpPicker({
  items,
  openingFileId = null,
  onOpenFile,
  onPlanSelected,
}: Props) {
  const [selectedId, setSelectedId] = useState('')
  const singleItem = items.length === 1 ? items[0]! : null
  const selected =
    singleItem ?? items.find((item) => item.id === selectedId) ?? null
  const busy = !!openingFileId
  const viewDisabled = busy || !selected

  return (
    <section className="company-erp-picker" aria-label="Emergency Response Plan">
      <div className="company-erp-picker-card">
        <label className="company-erp-picker-field">
          <span className="company-erp-picker-label">Emergency Response Plan</span>
          {items.length > 1 ? (
            <select
              className="company-erp-picker-select"
              value={selectedId}
              disabled={busy}
              onChange={(e) => {
                setSelectedId(e.target.value)
                const item = items.find((row) => row.id === e.target.value)
                if (item) onPlanSelected?.(item)
              }}
            >
              <option value="">Select a plan…</option>
              {items.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
          ) : (
            <div className="company-erp-picker-value" aria-readonly="true">
              {singleItem?.label ?? '—'}
            </div>
          )}
        </label>
        <button
          type="button"
          className="company-erp-picker-view-btn"
          disabled={viewDisabled}
          aria-busy={!!(selected && openingFileId === selected.id)}
          onClick={() => {
            if (!selected) return
            void onOpenFile(selected)
          }}
        >
          <span className="company-erp-picker-view-btn-inner">
            <span>VIEW SELECTED ERP</span>
            {selected && openingFileId === selected.id ? (
              <InlineSpinner label="Opening file" />
            ) : null}
          </span>
        </button>
      </div>
    </section>
  )
}
